import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Groupper, Icon, Placeholder } from 'react-frontier';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { QueuePlace, SetLoading } from '../PublicClasses';
import { useWakeLock } from '../Hooks';
import { Countdown } from '../components';
import NumberFlow from '@number-flow/react';
import API, { IS_DEBUG } from '../API';
import classNames from 'classnames';

const FATAL_CODES = ['INVINF', 'INVJTK', 'TKCCL', 'TKEXP'];
const MIN_TIMER = parseInt(process.env.REACT_APP_CHECK_TIMER) || 20000, TIMER_INTERVAL = parseInt(process.env.REACT_APP_CHECK_RANDOM) || 5000;
const INITIAL_TIMER = parseInt(process.env.REACT_APP_INITIAL_TIMER) || 5000;
const ERROR_THRESHOLD = IS_DEBUG ? 10 : 3;
const PROCESSING_ERROR_THRESHOLD = IS_DEBUG ? 5 : 3;
const MAX_CHECK_COUNT = 5;

function randomRange(min: number, max: number){
	return Math.floor(Math.random() * (max-min+1)+min);
}

var QueueWait = ()=>{
	var params = useParams<{ token: string }>();
	var WakeLock = useWakeLock({ reacquireOnPageVisible: true, })
	var { t } = useTranslation();
	var [place, setPlace] = useState<QueuePlace>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [checkTimer, setCheckTimer] = useState(null);
	var [errorCount, setErrorCount] = useState<number>(0);
	var [checkCount, setCheckCount] = useState<number>(0);
	var [searchParams] = useSearchParams();
	var errorCountRef = useRef<typeof errorCount>(null);
	var placeRef = useRef<typeof place>(null);
	var checkTimerRef = useRef<typeof checkTimer>(null);
	var checkCountRef = useRef<typeof checkCount>(null);
	var loadingRef = useRef<typeof loading>(null);
	loadingRef.current = loading;
	errorCountRef.current = errorCount;
	placeRef.current = place;
	checkTimerRef.current = checkTimer;
	checkCountRef.current = checkCount;

	var website_id = useMemo(()=>{
		var w = searchParams.get('w');
		if(!w || Number.isNaN(parseInt(w))) return null;
		else return parseInt(w);
	}, [params]);

	var valid_params = useMemo(()=>{
		return params && params.token && params.token.length>=32;
	}, [params?.token]);

	useEffect(()=>{
		if(place && WakeLock.isSupported){
			WakeLock.request();
		}
		return ()=>{
			if(WakeLock.isSupported) WakeLock.release();
		}
	}, [place]);

	var onCheckError = (message: string, code: string)=>{
		var fatal = code && FATAL_CODES.indexOf(code)!=-1;
		if(fatal || (errorCountRef.current+1)>=ERROR_THRESHOLD){
			setLoadError(message);
			if(checkTimerRef.current) clearInterval(checkTimerRef.current);
		}
		setErrorCount(errorCountRef.current+1);
	}

	var onProcessingError = (message: string, code: string)=>{
		var fatal = code && FATAL_CODES.indexOf(code)!=-1;
		if(fatal || (errorCountRef.current+1)>=PROCESSING_ERROR_THRESHOLD){
			setLoadError(message)
		}else{
			setTimeout(sendProcessing, randomRange(2000, 5000));
		}
		setErrorCount(errorCountRef.current+1);
	}

	var stopChecking = ()=>{
		if(checkTimerRef.current) clearInterval(checkTimerRef.current);
		setCheckTimer(null);
	}

	var sendProcessing = (token: string)=>{
		if(!token){
			if(!placeRef.current?.finish_token) return setLoadError(t('finish.no_token'));
			token = placeRef.current.finish_token
		}
		API.sendProcessing(token).then(res=>{
			if(res.error) return onProcessingError(res.message, res.code);
			if(res.data.redirect_url){
				setTimeout(()=>{
					window.location.href = res.data.redirect_url;
				}, 2000);
			}else{
				onProcessingError(t('common.unexpected_error', { code: 'QUWTSP-2' }), null)
			}
		}).catch(err=>{
			onProcessingError(t('common.unexpected_error', { code: 'QUWTSP-1' }), null)
		})
	}

	var checkQueue = ()=>{
		if(!params || !params.token || params.token.length<32 || loadingRef?.current===true) return;
		setLoading(true);
		API.getPlace(params.token).then(res=>{
			if(res.error) return onCheckError(res.message, res.code);
			var new_place = placeRef.current?.place;
			if(res.data.place){
				new_place = res.data.place
			}
			if(new_place) new_place = Math.max(1, new_place);

			setErrorCount(0);
			setPlace({
				...res.data,
				place: new_place || null,
				date_checked: Math.floor(new Date().getTime()/1000),
			});
			if(!res.data.date_start){
				setCheckCount((checkCountRef.current || 0)+1);
			}

			if(res.data.finish_token){
				sendProcessing(res.data.finish_token);
			}else if(res.data.redirect_url){
				setTimeout(()=>{
					window.location.href = res.data.redirect_url;
				}, 2000);
			}

			if(res.data.finish_token || res.data.redirect_url || res.data.rejoin_token){
				stopChecking();
			}
		}).catch(err=>{
			onCheckError(t('common.unexpected_error', { code: 'QUWTCK-1' }), null);
		}).finally(()=>{
			setLoading(false);
		})
	}

	useEffect(()=>{
		if(!params || !params.token || params.token.length<32) return;
		if(checkTimer) clearInterval(checkTimer);

		var timer = setInterval(()=>{
			checkQueue();
		}, randomRange(MIN_TIMER, MIN_TIMER+TIMER_INTERVAL));
		setCheckTimer(timer);

		var first_check = setTimeout(()=>{
			checkQueue();
		}, randomRange(INITIAL_TIMER, INITIAL_TIMER+1000));

		return ()=>{
			if(first_check) clearTimeout(first_check);
			clearInterval(timer);
			setCheckTimer(null);
		}
	}, []);

	var is_finished = useMemo(()=>{
		return !!place && (!!place.finish_token || !!place.redirect_url);
	}, [place]);

	var has_countdown = useMemo(()=>{
		return !!place && !!place?.date_start;
	}, [place, place?.date_start]);
	
	return <div className="ca wait-screen">
		<Groupper title={has_countdown ? t('common.waiting_room') : t('common.queue')} titleCentered>
			<div className="contents">
				{(!valid_params || loadError) ? <>
					<div className="marker error">
						<Icon name='triangle-exclamation' />
					</div>
					<div className="title">
						{t('common.error')}
					</div>
					<div className="description error">
						{!valid_params ? (
							t('wait.invalid_token')
						) : <>
							{loadError}
							<br />
							<Button text={t('common.retry')} size='small' style={{ marginTop: 10, minWidth: 150 }} onClick={(setLoading: SetLoading)=>{{
								setLoading(true);
								setTimeout(()=>{
									setLoading(false);
									window.location.reload()
								}, randomRange(2000, 5000));
							}}} />
						</>}
					</div>
				</> : <>
					<div className={classNames("marker", {
						gray: !place?.place && !is_finished,
						green: is_finished,
						red: place?.place && place?.rejoin_token && !is_finished
					})}>
						{(place!==null) ? (
							(is_finished ? (
								<Icon name='hands-clapping' />
							) : place?.rejoin_token ? (
								<Icon name='face-frown' />
							) : checkCountRef.current>=MAX_CHECK_COUNT && place?.place ? (
								<div className="number">
									<NumberFlow value={place?.place} />
								</div>
							) : has_countdown ? (
								<Countdown className='countdown' unix={place.date_start} renderFinish={(
									<Icon name='users' />
								)} />
							) : (
								<Icon name='users' />
							))
						) : (
							place ? (
								<Icon name='users' />
							) : (
								<Icon name='clock' />
							)
						)}
						{/* <div className="progress"></div> */}
					</div>
					<div className="title">
						{place ? (
							t(is_finished ? 'finish.title' : place?.rejoin_token ? 'wait.expired_title' : (has_countdown ? 'wait.waiting_start' : 'wait.title'))
						) : (
							<Placeholder height={30} />
						)}
					</div>
					<div className="description">
						<Trans i18nKey={is_finished ? 'finish.description' : place?.rejoin_token ? 'wait.expired_description' : (has_countdown ? 'wait.waiting_description' : 'wait.description')} />
						{/* {place ? (
							<Trans i18nKey={is_finished ? 'finish.description' : place?.rejoin_token ? 'wait.expired_description' : (has_countdown ? 'wait.waiting_description' : 'wait.description')} />
						) : (
							<div className="placeholder">
								<div className="fr inline loading"></div>
								<div className="text">{t('wait.joining')}</div>
							</div>
						)} */}
					</div>
					{place?.rejoin_token && (
						<Button color='red' text='Reiniciar' as={Link} to={`/rj/${place?.rejoin_token}?w=${website_id}`} />
					)}
					{is_finished && (
						<div className="fr inline loading"></div>
					)}
				</>}
			</div>
		</Groupper>
		<div className="bottom">
			<div className="item">
				{(IS_DEBUG && loading) ? (
					<div className="fr loading inline small"></div>
				) : (
					<Icon name='key' />
				)}
				{place?.client_id ? (
					IS_DEBUG ? place.client_id : place.client_id.toString(16).toUpperCase()
				) : (
					<Placeholder width={15} />
				)}
			</div>
			{IS_DEBUG && <>
				<div className="item">
					<Icon name='triangle-exclamation' /> {errorCount} / {is_finished ? PROCESSING_ERROR_THRESHOLD : ERROR_THRESHOLD}
				</div>
				<div className="item">
					<Icon name='hashtag' /> {checkCount} / {MAX_CHECK_COUNT}
				</div>
				{!checkTimer && (
					<div className="item">
						<Icon name='clock' /> Stopped
					</div>
				)}
			</>}
			<div className="item">
				Powered by Cardio
			</div>
		</div>
	</div>
}
export default QueueWait;